import { Textbox } from "@mecha/design-components/dist/src/components/Elements/Textbox";
import React, { useState } from "react";
import CautiousIcon from "../../../assets/svg/CautiousIcon";
import Loader from "../../../assets/svg/LoaderIcon";
import { ErrorReporter } from "../../../libs/ErrorReporter";
import Logger from "../../../libs/Logger";
import { Button } from "@mecha/design-components/dist/src/components/Elements/Button";
import ExclamationSquareIcon from "../../../assets/svg/ExclamationSquareIcon";
import ExclamationCircleIcon from "../../../assets/svg/ExclamationCircleIcon";
import dayjs from "dayjs";

interface DeleteApiKeyProps {
  name: string;
  setDelete: (x: boolean) => void;
  apiKeyId: string;
  handleDelete: (id: string) => void;
  expiryDate: string;
}

const DeleteApiKey = (props: DeleteApiKeyProps) => {
  const log = new Logger({ module: "Settings", component: "DeleteApiKey" });
  log.trace({ props }, "(DeleteApiKey) - props in DeleteApiKey component");
  const { name, setDelete, apiKeyId, handleDelete, expiryDate } = props;
  const [loading, setLoading] = useState(false);
  const [apiKeyName, setApiKeyName] = useState("");
  const [error, setError] = useState({ name: "" });

  const deleteApiKeyHandler = async () => {
    log.info(
      {},
      `(deleteApiKeyHandler) - user is deleting api key {apiKeyId: ${apiKeyId}}`,
    );
    setLoading(true);
    try {
      if (apiKeyName !== name) {
        setError(prevState => ({
          ...prevState,
          name: "Please enter same API Key name",
        }));
        setLoading(false);
        log.warn(
          { name: name, nameTypedInTextbox: apiKeyName },
          "(deleteApiKeyHandler) - User input wrong name while conforming to delete",
        );
        return;
      }
      handleDelete(`${apiKeyId}`);
    } catch (error) {
      log.error(
        { error: error },
        `(deleteApiKeyHandler) - error in deleting api key {apiKeyId: ${apiKeyId}}`,
      );
      ErrorReporter.collect(
        error,
        `Error in deleting API Key {apiKeyId: ${apiKeyId}}`,
      );
    }
    setDelete(false);
    setLoading(false);
  };

  return (
    <div className="w-full flex flex-col items-start justify-start mt-2">
      <div className="flex flex-row gap-2 items-start w-full p-4 rounded-md text-web-b5 dark:text-dark-n-200 text-light-n-200 dark:bg-dark-r-520 bg-light-r-520">
        <ExclamationSquareIcon className="dark:text-dark-r-500 text-light-r-500  w-4 h-4 mt-1" />
        <div>
          <p>
            <span>This action cannot be undone, be careful!</span>
          </p>
          <p className="mt-1">
            To confirm deletion, please re-enter the name of the API Key
          </p>
        </div>
      </div>
      <div className="w-full mt-5">
        <p className="font-medium text-web-b5 dark:text-dark-n-500 text-light-n-500">
          API Key
        </p>
        <div className="relative flex flex-row items-center">
          <div className="mt-[6px] w-full px-3 py-[10px] dark:bg-dark-760 bg-light-760 rounded-md grid grid-cols-[4fr_1fr] gap-2 items-center">
            <div className=" overflow-x-auto mecha-scroll-2">
              <p
                className="text-web-b4 dark:text-dark-n-200 text-light-n-200"
                title={name}
              >
                {name}
              </p>
            </div>

            <p className="text-web-b5 dark:text-dark-n-500 text-light-n-500 __text-expiry-date-onsucess text-justify whitespace-nowrap">
              {expiryDate
                ? `Expires on ${dayjs(expiryDate)?.format("DD MMM ,YYYY")}`
                : "No Expiry"}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col mt-5">
        <p className="font-medium text-web-b5 dark:text-dark-n-500 text-light-n-500">
          Re-enter key name
        </p>
        <Textbox
          boxSize="m"
          className="__input-api-key-confirm-name mt-[6px]"
          fullWidth
          maxLength={100}
          placeholder="Type here"
          onChange={e => {
            setApiKeyName(e.target.value);
            setError({ name: "" });
          }}
          style={error.name ? { borderColor: "#F4333F" } : {}}
        />
        {error.name && (
          <div className="flex flex-row gap-1 items-center mt-1">
            <span className=" dark:text-black text-storm-dust-110">
              <ExclamationCircleIcon
                className="h-3 w-3 dark:text-bg-dark-r-500 text-light-r-500"
                fill="transparent"
              />
            </span>
            <span>
              <p className="dark:text-dark-n-400 text-light-n-400 text-xs __input-error-name-required">
                {error.name}
              </p>
            </span>
          </div>
        )}
      </div>
      <div className="w-full flex md:flex-row items-center justify-end mt-10 flex-col md:mt-20  gap-2">
        <Button
          type="button"
          color="secondary-default"
          disabled={loading ? true : false}
          onClick={() => {
            setDelete(false);
          }}
          className=" __button-cancel-delete-api-key w-full md:w-max"
        >
          Cancel
        </Button>
        {!loading && (
          <Button
            type="button"
            color="danger"
            onClick={() => {
              deleteApiKeyHandler();
            }}
            className="__button-delete-api-key w-full md:w-max"
          >
            I understand, delete this key
          </Button>
        )}
        {loading && (
          <button
            onClick={() => {
              deleteApiKeyHandler();
            }}
            className={`relative flex items-center justify-start normal-case box-border no-underline appearance-none transition ease-in-out duration-150 focus:outline-none px-4.5 py-1.5 h-9 font-medium inline-block w-auto text-sm rounded-md text-white dark:text-white "bg-mecha-red-500 lg:hover:opacity-60 cursor-pointer ${
              loading ? "cursor-not-allowed" : "cursor-default"
            }`}
          >
            <Loader className="inline w-6 h-6 text-white animate-spin" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DeleteApiKey;
